// ═══════════════════════════════════════════════════════
// ProductCart — cosmetic-product-action.js
// ═══════════════════════════════════════════════════════
class ProductCart {
  constructor() {
    this.maxInventoryMap = {};
    this._cartPromise = null; // ✅ single shared cache
    this.cartNotification = document.querySelector('cart-notification')
  }

  getWrappers() {
    return document.querySelectorAll('.product-actions_wrapper');
  }

  getCardEls(wrapper) {
    return {
      addToBagEl:  wrapper.querySelector('.product-addToBag__action'),
      quantitySel: wrapper.querySelector('.quantity-selector_quantitySelector'),
      qtyInput:    wrapper.querySelector('.quantity-selector_quantityInput__ZG_I4'),
      decreaseBtn: wrapper.querySelector('[aria-label="Decrease quantity"]'),
      increaseBtn: wrapper.querySelector('[aria-label="Increase quantity"]'),
      addToBagBtn: wrapper.querySelector('.product-addToBag__action button')
    };
  }
  async updateDawnCartBubble(cart) {
    const totalQty = cart.item_count;

    const cartIconBubble = document.querySelector('#cart-icon-bubble');
    if (!cartIconBubble) return;

    let bubble = cartIconBubble.querySelector('.cart-count-bubble');

    if (totalQty > 0) {
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = `<span aria-hidden="true"></span><span class="visually-hidden"></span>`;
        cartIconBubble.appendChild(bubble);
      }
      bubble.querySelector('[aria-hidden]').textContent = totalQty;
      bubble.querySelector('.visually-hidden').textContent = `${totalQty} item${totalQty > 1 ? 's' : ''}`;
      bubble.style.display = '';

      const svg = cartIconBubble.querySelector('svg');
      if (svg) svg.classList.replace('icon-cart-empty', 'icon-cart');
    } else {
      bubble?.remove();
      const svg = cartIconBubble.querySelector('svg');
      if (svg) svg.classList.replace('icon-cart', 'icon-cart-empty');
    }
  }
  getVariantIdFromWrapper(wrapper) {
    return wrapper?.dataset?.variantId ?? null;
  }

  // ✅ Single cached fetch — all callers share one request
  async fetchCart() {
    if (!this._cartPromise) {
      this._cartPromise = fetch('/cart.js')
        .then(r => r.json())
        .catch(() => ({ items: [] }));

      // Auto-clear after 2s so next action gets fresh data
      setTimeout(() => { this._cartPromise = null; }, 2000);
    }
    return this._cartPromise;
  }

  // ✅ Call this after any cart mutation
  invalidateCart() {
    this._cartPromise = null;
  }

  getVariantInventory(variantId) {
    const data = document.querySelector(`.hh-inventory-information[data-id="${variantId}"]`);
    if (!data) return Infinity;
    if (data.dataset.inventoryManagement !== 'shopify') return Infinity;
    if (data.dataset.inventoryPolicy === 'continue') return Infinity;
    const quantity = parseInt(data.dataset.inventoryQuantity, 10);
    return isNaN(quantity) ? Infinity : quantity;
  }

  getCartItem(cartItems, variantId) {
    return cartItems.find(i => String(i.variant_id) === String(variantId)) ?? null;
  }

  updateIncreaseBtn(increaseBtn, currentQty, maxInventory) {
    if (!increaseBtn) return;
    const atMax = currentQty >= maxInventory;
    increaseBtn.disabled      = atMax;
    increaseBtn.style.opacity = atMax ? '0.4' : '1';
    increaseBtn.style.cursor  = atMax ? 'not-allowed' : 'pointer';
  }

  setLoadingState(quantitySel, isLoading) {
    if (!quantitySel) return;
    quantitySel.style.opacity       = isLoading ? '0.5' : '1';
    quantitySel.style.pointerEvents = isLoading ? 'none' : 'auto';
  }

  updateWrapperUI(wrapper, variantId, cartItems) {
    const { addToBagEl, quantitySel, qtyInput, increaseBtn } = this.getCardEls(wrapper);
    const cartItem     = this.getCartItem(cartItems, variantId);
    const maxInventory = this.maxInventoryMap[variantId] ?? Infinity;

    if (cartItem) {
      addToBagEl?.style.setProperty('display', 'none');
      quantitySel?.style.setProperty('display', 'flex');
      if (qtyInput) qtyInput.value = cartItem.quantity;
      this.updateIncreaseBtn(increaseBtn, cartItem.quantity, maxInventory);
    } else {
      addToBagEl?.style.setProperty('display', 'block');
      quantitySel?.style.setProperty('display', 'none');
      if (qtyInput) qtyInput.value = 1;
      this.updateIncreaseBtn(increaseBtn, 1, maxInventory);
    }
  }

  updateAllMatchingWrappers(variantId, cartItems) {
    this.getWrappers().forEach(wrapper => {
      if (String(this.getVariantIdFromWrapper(wrapper)) === String(variantId)) {
        this.updateWrapperUI(wrapper, variantId, cartItems);
      }
    });
  }
  async updateCartQuantity(variantId, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity }),
      });
      const data = await response.json();

      if (data.status) {
        console.error(data.message);
        return;
      }

      this.cartNotification.fetchCartDetails(true);
      this.invalidateCart();

      // ✅ Fresh cart fetch kar aur bubble update
      const cart = await this.fetchCart();
      await this.updateDawnCartBubble(cart);

    } catch (err) {
      console.error('Cart change error:', err);
    }
  }
  
  async removeFromCart(variantId) {
    await this.updateCartQuantity(variantId, 0);
    const cart = await this.fetchCart();
    await this.updateDawnCartBubble(cart)
    this.updateAllMatchingWrappers(variantId, cart.items);
  }

  // ─── Attach listeners to a single wrapper ────────────────────────────────
  attachListenersToWrapper(wrapper) {
    if (wrapper.dataset.cartInitialized === 'true') return;
    wrapper.dataset.cartInitialized = 'true';

    const { quantitySel, qtyInput, increaseBtn, decreaseBtn, addToBagBtn } = this.getCardEls(wrapper);

    // ── Add to Bag ──
    addToBagBtn?.addEventListener('click', async () => {
      const variantId    = this.getVariantIdFromWrapper(wrapper);
      const maxInventory = this.maxInventoryMap[variantId] ?? Infinity;
      if (!variantId || maxInventory <= 0) return;

      addToBagBtn.disabled = true;
      try {
        await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: variantId, quantity: 1 }),
        });
        this.invalidateCart(); // ✅
        const cart = await this.fetchCart();
        this.cartNotification.fetchCartDetails(true);
        this.updateAllMatchingWrappers(variantId, cart.items);
        await this.updateDawnCartBubble(cart)
      } catch (err) {
        console.error('Add to cart error:', err);
      } finally {
        addToBagBtn.disabled = false;
      }
    });

    // ── Increase ──
    increaseBtn?.addEventListener('click', async () => {
      const variantId    = this.getVariantIdFromWrapper(wrapper);
      const maxInventory = this.maxInventoryMap[variantId] ?? Infinity;
      const current      = parseInt(qtyInput.value, 10);

      
      if (current >= maxInventory) {
        this.updateIncreaseBtn(increaseBtn, current, maxInventory);
        return;
      }

      const newQty = current + 1;

      // Optimistic UI update
      this.getWrappers().forEach(w => {
        if (String(this.getVariantIdFromWrapper(w)) === String(variantId)) {
          const els = this.getCardEls(w);
          if (els.qtyInput) els.qtyInput.value = newQty;
          this.updateIncreaseBtn(els.increaseBtn, newQty, maxInventory);
        }
      });

      this.setLoadingState(quantitySel, true);
      try {
        await this.updateCartQuantity(variantId, newQty); // invalidates inside
      } finally {
        this.setLoadingState(quantitySel, false);
      }
    });

    // ── Decrease ──
    decreaseBtn?.addEventListener('click', async () => {
      const variantId = this.getVariantIdFromWrapper(wrapper);
      const current   = parseInt(qtyInput.value, 10);

      if (current <= 1) {
        await this.removeFromCart(variantId);
      } else {
        const newQty       = current - 1;
        const maxInventory = this.maxInventoryMap[variantId] ?? Infinity;

        this.getWrappers().forEach(w => {
          if (String(this.getVariantIdFromWrapper(w)) === String(variantId)) {
            const els = this.getCardEls(w);
            if (els.qtyInput) els.qtyInput.value = newQty;
            this.updateIncreaseBtn(els.increaseBtn, newQty, maxInventory);
          }
        });

        this.setLoadingState(quantitySel, true);
        try {
          await this.updateCartQuantity(variantId, newQty); // invalidates inside
        } finally {
          this.setLoadingState(quantitySel, false);
        }
      }
    });
  }

  reinitWrappers() {
    this.getWrappers().forEach(wrapper => {
      delete wrapper.dataset.cartInitialized;

      const variantId = this.getVariantIdFromWrapper(wrapper);
      if (variantId) {
        this.maxInventoryMap[variantId] = this.getVariantInventory(variantId);
      }

      this.attachListenersToWrapper(wrapper);
    });

    // ✅ invalidate so reinit always gets fresh cart
    this.invalidateCart();
    this.fetchCart().then(cart => {
      this.getWrappers().forEach(wrapper => {
        const variantId = this.getVariantIdFromWrapper(wrapper);
        if (variantId) this.updateWrapperUI(wrapper, variantId, cart.items);
      });
    });
  }

  async checkOnLoad() {
    const cart = await this.fetchCart(); // ✅ shared — no extra request
    this.getWrappers().forEach(wrapper => {
      const variantId = this.getVariantIdFromWrapper(wrapper);
      // console.log(variantId);
      if (!variantId) return;
      
      this.maxInventoryMap[variantId] = this.getVariantInventory(variantId);
      this.updateWrapperUI(wrapper, variantId, cart.items);
    });
  }

  attachVariantListeners() {
    document.querySelectorAll(
      '.product-shade-selector_shadeSelectorButton__RJJvK, .product-shade-selector_selectedShadeSelectorButtonv'
    ).forEach(btn => {
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.id;
        if (!variantId) return;
        // console.log(variantId);
        
        this.maxInventoryMap[variantId] = this.getVariantInventory(variantId);
        const cart = await this.fetchCart(); // ✅ cached — no extra request
        this.updateAllMatchingWrappers(variantId, cart.items);
      });
    });
  }

  init() {
    this.getWrappers().forEach(wrapper => this.attachListenersToWrapper(wrapper));
    this.checkOnLoad();
    this.attachVariantListeners();
  }
}

// ─── Boot ────────────────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  window.productCart = new ProductCart();
  window.productCart.init();
});